import React from 'react';

import { ContentPublicationStatus } from '@lumapps/contents/constants';
import { useDataAttributes } from '@lumapps/data-attributes';
import { CONTENT_EDITOR_TOPBAR_SCOPE } from '@lumapps/designer/constants';
import { DESIGNER } from '@lumapps/designer/keys';
import { Menu } from '@lumapps/lumx-menu/components/Menu';
import {
    mdiMenuDown,
    mdiArchiveArrowDownOutline,
    mdiArrowDown,
    mdiArrowUp,
    mdiClockEnd,
    mdiClose,
    mdiFileEditOutline,
} from '@lumapps/lumx/icons';
import { IconButton } from '@lumapps/lumx/react';
import { GLOBAL, useTranslate } from '@lumapps/translations';

interface SecondaryActionsMenuProps {
    isDisabled?: boolean;
    isHomepage?: boolean;
    onArchive: () => void;
    publicationStatus: ContentPublicationStatus;
    canArchive: boolean;
    canPublish: boolean;
    isAwaitingValidation: boolean;
    unpublishContent: () => void;
    publishContentNow: () => void;
    requestRevision: () => void;
    editContentScheduling: () => void;
    scheduleContent: () => void;
    unscheduleContent: () => void;
}

export const SecondaryActionsMenu: React.FC<SecondaryActionsMenuProps> = ({
    onArchive,
    isDisabled,
    publicationStatus,
    unpublishContent,
    canArchive,
    canPublish,
    isAwaitingValidation,
    publishContentNow,
    editContentScheduling,
    scheduleContent,
    unscheduleContent,
    requestRevision,
    isHomepage,
}) => {
    const { translateKey } = useTranslate();
    const { get } = useDataAttributes(CONTENT_EDITOR_TOPBAR_SCOPE);

    const displayPublishNowOption = publicationStatus === 'scheduled' && canPublish;
    const displayEditSchedulingOption =
        (publicationStatus === 'scheduled' || publicationStatus === 'expired') && canPublish;
    const displayScheduleOption = publicationStatus === 'draft' && canPublish;
    const displayUnpublishOption = publicationStatus === 'published' && canPublish && !isHomepage;
    const displayRequestRevisionOption = isAwaitingValidation;
    const displayUnscheduleOption = publicationStatus === 'scheduled' && canPublish;
    const displayArchiveOption = canArchive && !isHomepage;

    if (
        !displayArchiveOption &&
        !displayUnpublishOption &&
        !displayPublishNowOption &&
        !displayEditSchedulingOption &&
        !displayScheduleOption &&
        !displayRequestRevisionOption &&
        !displayUnscheduleOption
    ) {
        return null;
    }

    return (
        <Menu.Trigger>
            <Menu.TriggerButton
                as={IconButton}
                icon={mdiMenuDown}
                aria-disabled={isDisabled}
                label={translateKey(GLOBAL.MORE_ACTIONS)}
                {...get({ element: 'publication-workflow', action: 'show-more-options' })}
            />
            <Menu>
                <Menu.Section>
                    {displayPublishNowOption && (
                        <Menu.Item
                            onClick={publishContentNow}
                            icon={mdiArrowUp}
                            {...get({ element: 'publication-workflow', action: 'publish-now' })}
                        >
                            {translateKey(GLOBAL.PUBLISH_NOW)}
                        </Menu.Item>
                    )}
                    {displayEditSchedulingOption && (
                        <Menu.Item
                            icon={mdiClockEnd}
                            {...get({ element: 'publication-workflow', action: 'edit-scheduling' })}
                            onClick={editContentScheduling}
                        >
                            {translateKey(DESIGNER.EDIT_SCHEDULING)}
                        </Menu.Item>
                    )}
                    {displayScheduleOption && (
                        <Menu.Item
                            {...get({ element: 'publication-workflow', action: 'schedule' })}
                            icon={mdiClockEnd}
                            onClick={scheduleContent}
                        >
                            {translateKey(GLOBAL.SCHEDULE)}
                        </Menu.Item>
                    )}
                </Menu.Section>
                <Menu.Section>
                    {displayRequestRevisionOption && (
                        <Menu.Item
                            onClick={requestRevision}
                            icon={mdiFileEditOutline}
                            {...get({ element: 'publication-workflow', action: 'request-revision' })}
                        >
                            {translateKey(DESIGNER.REQUEST_REVISION_LABEL)}
                        </Menu.Item>
                    )}
                    {displayUnscheduleOption && (
                        <Menu.Item
                            onClick={unscheduleContent}
                            icon={mdiClose}
                            {...get({ element: 'publication-workflow', action: 'unschedule' })}
                        >
                            {translateKey(DESIGNER.UNSCHEDULE)}
                        </Menu.Item>
                    )}
                    {displayUnpublishOption && (
                        <Menu.Item
                            onClick={unpublishContent}
                            icon={mdiArrowDown}
                            {...get({ element: 'publication-workflow', action: 'unpublish' })}
                        >
                            {translateKey(DESIGNER.UNPUBLISH)}
                        </Menu.Item>
                    )}
                    {displayArchiveOption && (
                        <Menu.Item
                            icon={mdiArchiveArrowDownOutline}
                            onClick={onArchive}
                            {...get({ element: 'publication-workflow', action: 'archive' })}
                        >
                            {translateKey(GLOBAL.ARCHIVE)}
                        </Menu.Item>
                    )}
                </Menu.Section>
            </Menu>
        </Menu.Trigger>
    );
};
